import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Loops are the hardest",
  "description": "",
  "published": "2018-02-15T00:00:00.000Z",
  "redirect_from": ["/blog/loops-hardest/swizec/8191"],
  "categories": "Front End, Learning, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There is an old truism in software engineering that `}<a parentName="p" {...{
        "href": "https://blog.codinghorror.com/why-cant-programmers-program/"
      }}>{`199 out of 200`}</a>{` job applicants can't code. Like, forget fizz buzz, can't even write a loop.`}</p>
    <p>{`I don't know if that's true. Guess I got lucky. And I don't interview that much anyway.`}</p>
    <p>{`But I do work with a lot of brilliant engineers in my React + D3 workshops. Go into a company, meet with a team who wants to learn some new tech, show them around for a day or two, and create a better team.`}</p>
    <p>{`It's great.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Workshopin’s fun `}<a parentName="p" {...{
          "href": "https://t.co/3F2eTAMkBk"
        }}>{`pic.twitter.com/3F2eTAMkBk`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/963537340571398144"
      }}>{`February 13, 2018`}</a></blockquote>
    <p>{`My workshops involve a lot of coding. Practice makes perfect, and I find that a little struggle really helps the learning process.`}</p>
    <p>{`You know, code a bit, give them a challenge, let them solve it for a few minutes, show them how it's done. It engages their brain, lets my voice rest a bit, and makes the workshop more interactive.`}</p>
    <p>{`People love it.`}</p>
    <p>{`Wanna know what all these brilliant, gainfully employed engineers struggle with the most?`}</p>
    <p>{`My hardest example is a color swatch. I show them this:`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "https://codepen.io/swizec/pen/oYNvpQ/"
      }}>{`d3 color scale`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "https://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "https://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`It's a row of colorful rectangles that’s using D3 to get colors and React to render components in a loop.`}</p>
    <p>{`The idea is to show workshop participants how React makes code reuse simple, that D3 doesn't have to be scary, and that all the basic JavaScript and programming concepts work great. It's just code, the same kinda code you write all day.`}</p>
    <p>{`Then I ask them to turn this row into a checkerboard.`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "https://codepen.io/swizec/pen/BYdZXd/"
      }}>{`d3 color checkerboard`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "https://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "https://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`And I don't think anyone has ever solved it.`}</p>
    <p>{`We're not talking beginners here. These are hard-working, smart engineers. The kind of people who grok Redux in an afternoon. Learn React in a few hours. Keep `}<em parentName="p">{`massive`}</em>{` engineering infrastructure working smoothly every day. The kind of software projects that would make me shit my pants. They do it every day. It's easy.`}</p>
    <p>{`Systems, processes, really complex fucking things. All easy. Many have forgotten more about software engineering than I ever knew.`}</p>
    <p>{`Changing a loop into a nested loop? Impossible.`}</p>
    <p>{`I mean, the simplest solution to my challenge is changing this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{` render() {
    return (
      <g>
        {d3.range(20).map(i => (
          <swatch color={this.colors[i]} width={this.width.step()} x={this.width(i)} y="0">
        ))}
      </swatch></g>
    )
  }
`}</code></pre>
    <p>{`To this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{` render() {
    return (
      <g>
        {d3.range(20).map(j => d3.range(20).map(i => (
          <swatch color="{this.colors[j%2" ?="" i="" :="" this.colors.length-i-1]}="" width={this.width.step()} x={this.width(i)} y={j*20}>
        )))}
      </swatch></g>
    )
  }
`}</code></pre>
    <p>{`Wrap the iteration in another iteration, use that to change the `}<inlineCode parentName="p">{`y`}</inlineCode>{` coordinate. When you're on an even row, take colors from the left, take it from the right on odd rows.`}</p>
    <p>{`That's all. A nested loop problem.`}</p>
    <p>{`When we solve it together as a class, I usually create a `}<inlineCode parentName="p">{`<Row>`}</inlineCode>{` component and use that in a loop. Makes nesting easier to see and makes benefits of React more apparent.`}</p>
    <p>{`Maybe I have to change this example. Maybe loops `}<em parentName="p">{`are`}</em>{` hard and I'm just delusional.`}</p>
    <p>{`Do people get so used to solving huge engineering problems inside massive systems that they forget how to write code?`}</p>
    <p>{`I know designing systems is super hard and takes a special kind of brain. It's a whole different skill. How do you make this random process talk to that other mostly random process? How do you design your architecture so 50 engineers can work together? 1000 engineers? What about 10,000?`}</p>
    <p>{`But at the cost of forgetting how loops work? 🧐`}</p>
    <p>{`I'm both stumped and intrigued.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      